import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Box, Typography } from "@material-ui/core";
import { WarningMessage } from "@oriola-origo/origo-ui-core";
import { makeStyles } from "@material-ui/core/styles";
import { EditWarningIcon } from "../../../../images";
// eslint-disable-next-line import/no-cycle
import {
  updateCase,
  updateValidationError,
  setRequireMoreInfoFromCustomer,
  showDialog,
  hideDialog,
  setEditedKeyField,
  deleteCaseProperty,
} from "../../../../redux";
import { getValidationErrorMessage } from "../../../../utils/validations/validations";
import { YesNoSelection, YesNoSelectionValues } from "../../../common";
import { CaseType } from "../../../../constants/caseType";
import { isOriolaUser } from "../../../auth/permission";

const useStyles = makeStyles(() => ({
  italic: {
    fontStyle: "italic",
  },
}));

export const renderWarningMessage = (
  t,
  caseType,
  reasonIdentifier,
  SuspectedProductDefectReasonIdentifiers
) => {
  if (caseType === CaseType.DAMAGED_PRODUCT) {
    return (
      <Box mb={2} width="100%">
        <WarningMessage
          text={
            <Typography component="span">
              {t("mightAskReturn")}
              <br />
              <br />
              {t("addPicture1")}
              <br />
              <br />
              {t("addPicture2")}
            </Typography>
          }
          type="warning"
        />
      </Box>
    );
  }
  if (caseType === CaseType.SUSPECTED_PRODUCT_DEFECT && reasonIdentifier) {
    return (
      <Box mb={2} width="100%">
        <WarningMessage
          text={
            <Typography component="span">
              {reasonIdentifier ===
                SuspectedProductDefectReasonIdentifiers.FALSIFIED_MEDICINE &&
                t("noReturnForSpdForSFMD")}
              {[
                SuspectedProductDefectReasonIdentifiers.MEDICINE_PRODUCT,
                SuspectedProductDefectReasonIdentifiers.NON_MEDICINE_PRODUCT,
              ].includes(reasonIdentifier) && t("noReturnForSpdForOther")}
            </Typography>
          }
          type="warning"
        />
      </Box>
    );
  }
  return null;
};
function ProductReturn({ disabled, editMode, ...containerProps }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { editedKeyField } = useSelector(state => state.case);
  const { productReturn, caseType, reasonIdentifier, sourceReclamationId } =
    useSelector(state => state.case.case);
  const { returnProductError } = useSelector(state => state.validationErrors);
  const { SuspectedProductDefectReasonIdentifiers } = useSelector(
    state => state.fieldsConfig
  );
  const { userData } = useSelector(state => state.user);
  const contactInfo = useSelector(state => state.contactInfo);

  // Track previous shouldHideField state to detect transitions
  const prevShouldHideField = useRef(false);

  // Hide field for Oriola users in SPD cases when customer is also Oriola (customerId = "1")
  const shouldHideField =
    userData &&
    isOriolaUser(userData) &&
    caseType === CaseType.SUSPECTED_PRODUCT_DEFECT &&
    contactInfo?.customer?.customerId === "1";

  // Effect to handle field visibility changes and ensure correct default values
  useEffect(() => {
    // Calculate if field was hidden and is now visible inside the effect to avoid stale closures
    const wasHiddenNowVisible = prevShouldHideField.current && !shouldHideField;

    if (wasHiddenNowVisible) {
      // Field became visible - clear the value
      dispatch(
        updateCase({
          productReturn: null,
          confirm: false,
        })
      );
    } else if (shouldHideField && productReturn?.returnValue !== undefined) {
      // Field is hidden but has a value - set to undefined to ensure no default selection
      // Only do this if we're not in a copy case scenario (which might have pre-populated data)
      // Check if this is a copy case - sourceReclamationId exists or not. if exist meaning it's a copy case
      if (!sourceReclamationId) {
        dispatch(
          updateCase({
            productReturn: { ...productReturn, returnValue: undefined },
            confirm: false,
          })
        );
      }
    }
    // Update the ref for next render
    prevShouldHideField.current = shouldHideField;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldHideField, dispatch]);

  if (shouldHideField) {
    return null;
  }
  const getProductReturnValue = () => {
    if (productReturn == null || productReturn.returnValue == null) {
      return "";
    }
    return productReturn.returnValue === true
      ? YesNoSelectionValues.Yes
      : YesNoSelectionValues.No;
  };

  const updateProductReturnInEditMode = value => {
    dispatch(hideDialog());
    if (value && !productReturn.amount) {
      dispatch(setRequireMoreInfoFromCustomer(true));
      dispatch(
        updateCase({ productReturn: { returnValue: value }, confirm: false })
      );
    } else {
      dispatch(setRequireMoreInfoFromCustomer(false));
      dispatch(
        updateCase({
          productReturn: { returnValue: value, amount: productReturn.amount },
          confirm: true,
        })
      );
    }
  };

  const onChangeInEditMode = value => {
    if (caseType === CaseType.PRODUCT_DELIVERY && !editedKeyField) {
      dispatch(
        showDialog({
          open: true,
          description: t("editWarningDialog.description"),
          boldDescription: t("editWarningDialog.boldDescription"),
          icon: (
            <div>
              <EditWarningIcon color="primary" />
            </div>
          ),
          title: t("editWarningDialog.title"),
          cancelButtonText: t("editWarningDialog.cancel"),
          okButtonText: t("editWarningDialog.confirm"),
          onCancel: () => dispatch(hideDialog()),
          onOk: () => {
            updateProductReturnInEditMode(value);
            dispatch(setEditedKeyField(true));
          },
        })
      );
    } else {
      updateProductReturnInEditMode(value);
    }
  };

  return (
    <Box {...containerProps}>
      <YesNoSelection
        id="new-case-product-return"
        disabled={disabled}
        title={t("productReturnInfo")}
        error={!!returnProductError}
        errorText={getValidationErrorMessage(returnProductError, t)}
        value={getProductReturnValue()}
        onValueChanged={event => {
          const value = event.target.value === YesNoSelectionValues.Yes;
          dispatch(updateValidationError({ returnProductError: "" }));
          dispatch(deleteCaseProperty("suspectedInfectiousProduct"));
          if (editMode) {
            onChangeInEditMode(value);
          } else {
            dispatch(
              updateCase({
                productReturn: { returnValue: value },
                confirm: false,
              })
            );
          }
        }}
      />
      {productReturn && productReturn.returnValue === false && (
        <Box mb={2} className={classes.italic} width="100%">
          {renderWarningMessage(
            t,
            caseType,
            reasonIdentifier,
            SuspectedProductDefectReasonIdentifiers
          )}
        </Box>
      )}
    </Box>
  );
}

ProductReturn.propTypes = {
  disabled: PropTypes.bool,
  editMode: PropTypes.bool,
  hideConfirm: PropTypes.bool,
};

ProductReturn.defaultProps = {
  disabled: false,
  editMode: false,
  hideConfirm: false,
};

ProductReturn.displayName = "ProductReturn";

export default ProductReturn;
